// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import {
  change,
  Field,
  Form,
  formValueSelector,
  reduxForm,
  type FormProps,
} from 'redux-form';

import {
  ADD_EXTRACT_MAPPING_PANEL_ID,
  CREATE_FIELD_FORM,
} from 'util/constants';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import {
  addNotification,
  ERROR_LEVEL,
} from 'portfolio-shared/state/commands/globalNotificationsCommands';
import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';
import { actions as dialogPanelActions } from 'portfolio-shared/state/reducers/dialogPanelReducer';
import {
  createMetadataMappings,
  deleteMetadataMappings,
  getFields as getFieldsAction,
  removeAllPredefinedValues as removeAllPredefinedValuesAction,
  setEmbedMetadataMapping,
  updateFields as updateFieldsAction,
  updatePredefinedList,
} from 'state/commands/catalogCommands';
import type {
  Catalog,
  Dispatch,
  FieldDefinition,
  MetadataDefinition,
  MetadataMapping,
} from 'state/types';
import localizedStrings from 'util/localizedStrings';
import {
  getActivePanelId,
  getFields,
  getFieldNames,
  getSelectedField,
  getMetadataDefinitions,
  getMetadataMappings,
} from 'state/selectors';
import { FIELD_TYPE } from 'portfolio-shared/util/fieldDefUtils';
import {
  isDateField,
  isFieldDisabled,
  canHaveMultipleValues,
  canHavePredefinedValues,
  getEmbeddableMetadataOptions,
  getEmbedMappingMetadataDef,
  getEmbedMappingMetadataDefId,
  getExtractMetadataForField,
  ID_DISABLED_FIELD_DEF,
  isCharacterLegal,
  isFieldDefInSystemCategory,
  MAX_DECIMAL_PRECISION,
  MAX_STRING_MAX_LENGTH,
  MAX_SYSTEM_NAME_FIELD_LEN,
  MD_MAPPING_TYPE_EMBED,
  MD_MAPPING_TYPE_EXTRACT,
  NOT_FOUND,
} from 'util/fieldsPageUtils';
import CheckRadio from 'view/Forms/BulmaCheckRadio';
import {
  AddExtractMapping,
  ExtractMetadataMappingRow,
  MappingWarnings,
} from './FieldFormMappings';
import PredefinedList from './PredefinedList';
import { actions } from 'state/reducers/catalogReducer';

const { setSelectedFieldsListItemName } = actions;
const { showModalPrompt } = modalPromptActions;
const { hideDialogPanel, showDialogPanel } = dialogPanelActions;

const FIELD_TYPES = [
  FIELD_TYPE.STRING,
  FIELD_TYPE.NUMBER,
  FIELD_TYPE.DECIMAL,
  FIELD_TYPE.DATETIME,
  FIELD_TYPE.TEXTBLOCK,
  FIELD_TYPE.URL,
  FIELD_TYPE.BOOLEAN,
];

type Props = {
  activePanelId?: string,
  catalog: Catalog,
  field: FieldDefinition,
  fieldDefinitions: FieldDefinition[],
  intl: IntlShape,
  isCreate: ?boolean,
  getActivePanelId: typeof getActivePanelId,
  getFieldNames: typeof getFieldNames,
  getFieldsAction: typeof getFieldsAction,
  getMetadataMappings: typeof getMetadataMappings,
  getMetadataDefinitions: typeof getMetadataDefinitions,
  metadataDefinitions: MetadataDefinition[],
  metadataMappings: MetadataMapping[],
  removeAllPredefinedValuesAction: typeof removeAllPredefinedValuesAction,
  setSelectedFieldsListItemName: typeof setSelectedFieldsListItemName,
  updateFieldsAction: typeof updateFieldsAction,
  updatePredefinedList: typeof updatePredefinedList,
} & FormProps;

type State = {};

class FieldForm extends Component<Props, State> {
  addExtractMapping: Function;
  editPDL: Function;
  handleDefaultValueKeyPress: Function;
  handleHasPredefinedValuesCheckboxChange: Function;
  handleTextFieldOnKeyUp: Function;
  handleTextFieldOnKeyPress: Function;
  initializeFormValues: Function;
  makeMetadataMapping: Function;
  removeMapping: Function;
  renderFieldTypeData: Function;
  renderMetadataMappings: Function;
  renderLargeTextField: Function;
  renderTextField: Function;
  setEmbedMapping: Function;
  showAddExtractMappingSelect: Function;

  constructor() {
    super();
    this.addExtractMapping = this.addExtractMapping.bind(this);
    this.editPDL = this.editPDL.bind(this);
    this.handleDefaultValueKeyPress = this.handleDefaultValueKeyPress.bind(
      this
    );
    this.handleHasPredefinedValuesCheckboxChange = this.handleHasPredefinedValuesCheckboxChange.bind(
      this
    );
    this.handleTextFieldOnKeyPress = this.handleTextFieldOnKeyPress.bind(this);
    this.handleTextFieldOnKeyUp = this.handleTextFieldOnKeyUp.bind(this);
    this.initializeFormValues = this.initializeFormValues.bind(this);
    this.makeMetadataMapping = this.makeMetadataMapping.bind(this);
    this.removeMapping = this.removeMapping.bind(this);
    this.renderFieldTypeData = this.renderFieldTypeData.bind(this);
    this.renderMetadataMappings = this.renderMetadataMappings.bind(this);
    this.renderLargeTextField = this.renderLargeTextField.bind(this);
    this.renderTextField = this.renderTextField.bind(this);
    this.setEmbedMapping = this.setEmbedMapping.bind(this);
    this.showAddExtractMappingSelect = this.showAddExtractMappingSelect.bind(
      this
    );
  }

  componentDidMount() {
    const { field } = this.props;
    if (field) {
      this.initializeFormValues(field);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { field } = this.props;
    if (field !== nextProps.field) {
      this.initializeFormValues(nextProps.field || {});
    }
  }

  initializeFormValues(field) {
    let defaultValue = field.defaultValues ? field.defaultValues[0] : null;
    if (field.type === FIELD_TYPE.BOOLEAN) {
      defaultValue = defaultValue === true || defaultValue === 'true';
    }
    const maxLength = field.maxLength || 250;
    const precision = field.precision || 2;
    this.props.initialize({
      ...field,
      defaultValue,
      maxLength,
      precision,
    });
  }

  handleHasPredefinedValuesCheckboxChange(event) {
    const {
      isCreate,
      field,
      form,
      catalog,
      removeAllPredefinedValuesAction,
      updateFieldsAction,
    } = this.props;

    if (isCreate) {
      return;
    }

    async function doUpdate() {
      field.hasPredefinedValues = event.target.checked;
      await updateFieldsAction(catalog.catalogId, [field]);
      if (!field.hasPredefinedValues && field.predefinedValues.length > 0) {
        await removeAllPredefinedValuesAction(catalog.catalogId, [field.id]);
      }
    }

    if (!event.target.checked) {
      this.props.dispatch(
        showModalPrompt({
          dataTestName: 'confirm-delete-predefined-values',
          message: localizedStrings.CONFIRM_DELETE_PDL,
          onButton2: doUpdate,
          onButton1: () =>
            this.props.dispatch(change(form, 'hasPredefinedValues', true)),
        })
      );
    } else {
      doUpdate();
    }
  }

  handleDefaultValueKeyPress(e) {
    const { field } = this.props;
    if (!isCharacterLegal(e.key, field, e.target)) {
      e.preventDefault();
    }
  }

  handleTextFieldOnKeyPress(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.currentTarget.blur();
    }
  }

  async handleTextFieldOnKeyUp(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      const target = e.currentTarget;
      await this.props.reset();
      target.blur();
    }
  }

  makeMetadataMapping(fieldDefId, metadataDefId, mappingType): MetadataMapping {
    return {
      fieldDefId: fieldDefId,
      metadataDefId: metadataDefId,
      priority: 0, // unused
      type: mappingType,
    };
  }

  addExtractMapping(mdId) {
    const { catalog, createMetadataMappings, field } = this.props;
    const mappings = [
      this.makeMetadataMapping(field.id, mdId, MD_MAPPING_TYPE_EXTRACT),
    ];
    createMetadataMappings(catalog.catalogId, mappings);
  }

  removeMapping(fdId, mdId, type) {
    const { catalog, deleteMetadataMappings } = this.props;
    const mappings = [this.makeMetadataMapping(fdId, mdId, type)];
    deleteMetadataMappings(catalog.catalogId, mappings);
  }

  setEmbedMapping(event) {
    const {
      catalog,
      deleteMetadataMappings,
      field,
      metadataDefinitions,
      metadataMappings,
      setEmbedMetadataMapping,
    } = this.props;
    const catId = catalog.catalogId;
    const mdId = event.target.value;
    const mapping = this.makeMetadataMapping(
      field.id,
      mdId,
      MD_MAPPING_TYPE_EMBED
    );

    if (mdId === NOT_FOUND) {
      // delete existing embed
      mapping.metadataDefId = getEmbedMappingMetadataDefId(
        field.id,
        metadataDefinitions,
        metadataMappings
      );
      deleteMetadataMappings(catId, [mapping]);
    } else {
      // set new embed
      setEmbedMetadataMapping(catId, field.id, mapping);
    }
  }

  showAddExtractMappingSelect() {
    this.props.showDialogPanel(ADD_EXTRACT_MAPPING_PANEL_ID);
  }

  fieldTypeToLocalizedString(type) {
    switch (type) {
      case 1:
        return localizedStrings.FIELD_TYPE_NUMBER;
      case 2:
        return localizedStrings.FIELD_TYPE_DECIMAL;
      case 3:
        return localizedStrings.FIELD_TYPE_DATETIME;
      case 4:
        return localizedStrings.FIELD_TYPE_URL;
      case 5:
        return localizedStrings.FIELD_TYPE_TEXTBLOCK;
      case 7:
        return localizedStrings.FIELD_TYPE_BOOLEAN;
      case 0:
      default:
        return localizedStrings.FIELD_TYPE_STRING;
    }
  }

  editPDL(add, fieldDef, values, parentNestedValueId = null) {
    const { catalog } = this.props;
    this.props.updatePredefinedList(
      catalog.catalogId,
      fieldDef,
      add && values,
      !add && values,
      parentNestedValueId
    );
  }

  renderFieldTypeData(field) {
    const {
      intl: { formatMessage },
      isCreate,
    } = this.props;

    // String, decimal, and datetime fields have an extra form field.
    // Compute the type-dependent properties of the form field.
    let label: MessageDescriptor = localizedStrings.FIELD_FORM_LABEL_MAX_LENGTH;
    let formFieldName = '';
    switch (field.type) {
      case FIELD_TYPE.DECIMAL:
        label = localizedStrings.FIELD_FORM_LABEL_PRECISION;
        formFieldName = 'precision';
        break;
      case FIELD_TYPE.DATETIME:
        label = localizedStrings.FIELD_FORM_LABEL_SHOW_TIME;
        formFieldName = 'showTime';
        break;
      case FIELD_TYPE.STRING:
        formFieldName = 'maxLength';
        break;
      default:
    }
    const isDateField = 'showTime' === formFieldName;
    const isDecimalField = 'precision' === formFieldName;
    const fieldProps = {
      id: makeFieldId(formFieldName, isCreate),
      name: formFieldName,
      component: isDateField ? CheckRadio : 'input',
      type: '',
      onKeyPress: undefined,
      props: {},
      className: '',
      style: {},
      disabled: isFieldDisabled(field) || isFieldDefInSystemCategory(field),
    };
    if (isDateField) {
      fieldProps.type = 'checkbox';
      fieldProps.props = { className: 'checkradio-inline' };
    } else {
      fieldProps.className = 'input';
      fieldProps.style = { width: '70px' };
      fieldProps.onKeyPress = this.handleTextFieldOnKeyPress;
    }

    return (
      <span style={{ lineHeight: '40px' }}>
        <span key="type">
          <span key="label">
            {formatMessage(localizedStrings.FIELD_FORM_LABEL_FIELD_TYPE)}:{' '}
          </span>
          {isCreate ? (
            <span
              className="field select"
              style={{ verticalAlign: 'middle' }}
              data-test-name="field-type"
            >
              <Field component="select" id="field-type" name="type">
                {FIELD_TYPES.map(t => (
                  <option
                    key={'t' + t}
                    value={t}
                    data-test-name={'field-type-' + t}
                  >
                    {formatMessage(this.fieldTypeToLocalizedString(t))}
                  </option>
                ))}
              </Field>
            </span>
          ) : (
            <span key="value" className="has-text-dark">
              <FormattedMessage
                {...this.fieldTypeToLocalizedString(field.type)}
              />
            </span>
          )}
        </span>
        {formFieldName && (
          <span key="data" style={{ paddingLeft: '20px' }}>
            <span key="label">
              <FormattedMessage {...label} />:{' '}
            </span>
            <Field {...fieldProps} />
            {isDecimalField && (
              <span style={{ paddingLeft: '10px' }}>
                {formatMessage(localizedStrings.FIELD_FORM_LABEL_PRECISION_MAX)}
                : {MAX_DECIMAL_PRECISION}
              </span>
            )}
          </span>
        )}
      </span>
    );
  }

  renderMetadataMappings() {
    const {
      field,
      fieldDefinitions,
      intl,
      metadataDefinitions,
      metadataMappings,
    } = this.props;
    const allEmbeddables = getEmbeddableMetadataOptions(metadataDefinitions);
    const embedMetadata =
      field.id === ID_DISABLED_FIELD_DEF
        ? metadataDefinitions.find(md => md.displayName === field.systemName)
        : getEmbedMappingMetadataDef(
            field.id,
            metadataDefinitions,
            metadataMappings
          );
    const extractMetadata =
      field.id === ID_DISABLED_FIELD_DEF
        ? [metadataDefinitions.find(md => md.displayName === field.systemName)]
        : getExtractMetadataForField(
            field.id,
            metadataMappings,
            metadataDefinitions
          );
    const disabled: boolean = isFieldDisabled(field);
    const boxStyle = disabled
      ? { backgroundColor: 'var(--formDisabledBackgroundColor)' }
      : {};

    return (
      <div className="control box" style={boxStyle}>
        <div className="subtitle">
          <FormattedMessage
            {...localizedStrings.FIELD_FORM_LABEL_USE_METADATA}
          />
        </div>

        <div className="field fields-mapping-section is-indented-large">
          <label className="label">
            <FormattedMessage {...localizedStrings.FIELD_FORM_LABEL_EXTRACT} />
          </label>
          <div className="has-text-dark">
            {extractMetadata &&
              extractMetadata.length > 0 &&
              extractMetadata.map(
                md =>
                  md && (
                    <ExtractMetadataMappingRow
                      disabled={disabled}
                      fdId={field.id}
                      key={md.id}
                      md={md}
                      removeMapping={this.removeMapping}
                    />
                  )
              )}
            {!disabled && (
              <div className="fields-mapping-row label">
                <FormattedMessage
                  {...localizedStrings.FIELD_ADD_EXTRACT_METADATA}
                />
                <button
                  className="button is-primary is-inverted"
                  data-test-name="add-extract-mapping"
                  disabled={disabled}
                  onClick={this.showAddExtractMappingSelect}
                  type="button"
                >
                  <i className="fa fa-plus-circle" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="field fields-mapping-section is-indented-large">
          <label className="label">
            <FormattedMessage {...localizedStrings.FIELD_FORM_LABEL_EMBED} />
          </label>
          <div className="control fields-mapping-row">
            <div className="select is-fullwidth">
              <select
                disabled={disabled}
                onChange={this.setEmbedMapping}
                value={embedMetadata ? embedMetadata.id : NOT_FOUND}
                data-test-name="embed-into-select"
              >
                {allEmbeddables.map(e => (
                  <option
                    key={e.id}
                    value={e.id}
                    data-test-name="embed-into-field"
                  >
                    {e.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {embedMetadata && (
            <div className="is-indented">
              <MappingWarnings
                field={field}
                fieldDefinitions={fieldDefinitions}
                intl={intl}
                metadataDef={embedMetadata}
                metadataMappings={metadataMappings}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  renderLargeTextField(field) {
    return this.renderTextField(field, 'is-medium');
  }

  renderTextField(field, className) {
    const {
      disabled,
      input,
      maxLength,
      meta: { touched, error },
    } = field;
    return (
      <div>
        <input
          {...input}
          id={field.id}
          className={`input ${error ? 'is-danger' : ''} ${className}`}
          disabled={disabled}
          onKeyPress={this.handleTextFieldOnKeyPress}
          onKeyUp={this.handleTextFieldOnKeyUp}
          maxLength={maxLength}
        />
        {touched &&
          (error && (
            <div className="help is-danger">
              <FormattedMessage {...error} />
            </div>
          ))}
      </div>
    );
  }

  render() {
    const {
      activePanelId,
      field,
      hideDialogPanel,
      intl: { formatMessage },
      isCreate,
      metadataDefinitions,
      metadataMappings,
      showModalPrompt,
    } = this.props;
    const disabled: boolean = isFieldDisabled(field);
    // disallow changing a multi-valued field to a single-valued one
    const isMultivaluedDisabled: boolean =
      !isCreate && field && field.isMultivalued;
    const boxStyle = disabled
      ? { backgroundColor: 'var(--formDisabledBackgroundColor)' }
      : {};
    if (field) {
      return (
        <div
          className={`portfolio-form-container ${
            isCreate ? '' : 'is-textured'
          }`}
          data-test-name="field-form"
        >
          <Form onSubmit={() => {}} style={{ width: '100%' }}>
            <div
              className={`control ${isCreate ? '' : 'box'}`}
              style={boxStyle}
            >
              {!isCreate && isFieldDefInSystemCategory(field) ? (
                <div className="title is-4" style={{ margin: '0 5px' }}>
                  {field.systemName}
                </div>
              ) : (
                <Field
                  component={this.renderLargeTextField}
                  id={makeFieldId('systemName', isCreate)}
                  name="systemName"
                  disabled={disabled}
                  maxLength={MAX_SYSTEM_NAME_FIELD_LEN}
                />
              )}
              <div style={{ padding: '5px' }} data-test-name="field-type">
                {this.renderFieldTypeData(field)}
              </div>
              {!isDateField(field) && (
                <div>
                  <div key="defaultValueLabel" style={{ padding: '5px' }}>
                    <FormattedMessage
                      {...localizedStrings.FIELD_FORM_LABEL_DEFAULT_VALUE}
                    />
                  </div>
                  {field.type === FIELD_TYPE.BOOLEAN ? (
                    <Field
                      component={CheckRadio}
                      id={makeFieldId('defaultValue', isCreate)}
                      name="defaultValue"
                      disabled={disabled}
                      type="checkbox"
                    />
                  ) : field.type === FIELD_TYPE.NUMBER ||
                    field.type === FIELD_TYPE.DECIMAL ? (
                    <Field
                      className="input"
                      component="input"
                      id={makeFieldId('defaultValue', isCreate)}
                      name="defaultValue"
                      disabled={disabled}
                      type="number"
                      onKeyPress={this.handleDefaultValueKeyPress}
                    />
                  ) : field.type === FIELD_TYPE.TEXTBLOCK ? (
                    <Field
                      className="textarea"
                      component="textarea"
                      id={makeFieldId('defaultValue', isCreate)}
                      name="defaultValue"
                      disabled={disabled}
                    />
                  ) : (
                    <Field
                      component={this.renderTextField}
                      id={makeFieldId('defaultValue', isCreate)}
                      name="defaultValue"
                      disabled={disabled}
                      maxLength={field.maxLength || MAX_STRING_MAX_LENGTH}
                    />
                  )}
                </div>
              )}
              {canHaveMultipleValues(field) && (
                <Field
                  component={CheckRadio}
                  id={makeFieldId('is-multivalued', isCreate)}
                  label={formatMessage(
                    localizedStrings.FIELD_FORM_LABEL_HAS_MULTIPLE_VALUES
                  )}
                  name="isMultivalued"
                  type="checkbox"
                  disabled={disabled || isMultivaluedDisabled}
                />
              )}
              {canHavePredefinedValues(field) && (
                <Field
                  component={CheckRadio}
                  id={makeFieldId('has-predefined-values', isCreate)}
                  label={formatMessage(
                    localizedStrings.FIELD_FORM_LABEL_HAS_PREDEFINED_VALUES
                  )}
                  name="hasPredefinedValues"
                  type="checkbox"
                  disabled={disabled}
                  onChange={this.handleHasPredefinedValuesCheckboxChange}
                />
              )}
              {canHavePredefinedValues(field) && (
                <div style={{ marginLeft: '30px' }}>
                  <Field
                    component={CheckRadio}
                    id={makeFieldId('allow-only-predefined-values', isCreate)}
                    label={formatMessage(
                      localizedStrings.FIELD_FORM_LABEL_ONLY_PREDEFINED_VALUES
                    )}
                    name="usePredefinedOnly"
                    type="checkbox"
                    disabled={disabled || !field.hasPredefinedValues}
                  />
                </div>
              )}
            </div>
            {field.hasPredefinedValues && !isCreate && (
              <PredefinedList
                intl={this.props.intl}
                editPDL={this.editPDL}
                fieldDef={field}
                reduxFormInput={() => {}}
                showModalPrompt={showModalPrompt}
                submitFieldValue={() => {}}
              />
            )}
            {!isCreate && this.renderMetadataMappings()}
          </Form>
          <AddExtractMapping
            activePanelId={activePanelId}
            addExtract={this.addExtractMapping}
            field={field}
            hideDialogPanel={hideDialogPanel}
            formatMessage={formatMessage}
            mappings={metadataMappings}
            mds={metadataDefinitions}
          />
        </div>
      );
    } else {
      return (
        <div className="full-page-message">
          <FormattedMessage
            {...localizedStrings.FIELD_NO_FIELD_SELECTED_MESSAGE}
          />
        </div>
      );
    }
  }
}

function makeFieldId(id: string, isCreate: ?boolean) {
  return `${id}${isCreate ? '_create' : ''}`;
}

function validate(data: Object, props: Props) {
  const errors = {};
  const newName = data.systemName && data.systemName.trim();
  if (!props.isCreate && !newName) {
    errors.systemName = localizedStrings.REQUIRED_FIELD_ERROR;
  }
  if (props.isCreate && newName) {
    const found = props.fieldNames.find(
      old => old.toLowerCase() === newName.toLowerCase()
    );
    if (found) {
      errors.systemName = 'duplicate';
    }
  }
  return errors;
}

function asyncValidate(values, dispatch, props, blurredField) {
  if (!props.valid) {
    return new Promise((resolve, reject) => {
      props.reset();
      dispatch(
        addNotification(
          localizedStrings.NOTIFICATION_FAILURE_UPDATE,
          ERROR_LEVEL
        )
      );
    });
  }

  if (blurredField === 'defaultValue') {
    values['defaultValues'] =
      values.defaultValue === '' ? [] : [values.defaultValue.toString().trim()];
  }

  if (blurredField === 'systemName') {
    const newSystemName = (values['systemName'] = values['systemName'].trim());
    if (
      props.field['systemName'] !== newSystemName &&
      props.fieldNames.find(
        old => old.toLowerCase() === newSystemName.toLowerCase()
      )
    ) {
      /* eslint-disable no-throw-literal */
      return new Promise((resolve, reject) => {
        throw { systemName: localizedStrings.DUPLICATE_FIELD_NAME_ERROR };
      });
    } else {
      props.setSelectedFieldsListItemName(newSystemName);
      setTimeout(() => {
        const element = document.getElementById('fli-' + newSystemName);
        element && element.scrollIntoView();
      }, 250);
    }
  }

  return dispatch(updateFieldsAction(props.catalog.catalogId, [values]));
}

const editForm = reduxForm({
  asyncValidate,
  form: 'FieldForm',
  validate,
  asyncBlurFields: [
    'isMultivalued',
    'defaultValue',
    'maxLength',
    'precision',
    'showTime',
    'systemName',
    'usePredefinedOnly',
  ],
})(injectIntl(FieldForm));

const editFormState = state => ({
  activePanelId: getActivePanelId(state),
  field: getSelectedField(state),
  fieldDefinitions: getFields(state),
  fieldNames: getFieldNames(state),
  metadataDefinitions: getMetadataDefinitions(state),
  metadataMappings: getMetadataMappings(state),
});

const reduxCreateForm = reduxForm({
  form: CREATE_FIELD_FORM,
  validate,
})(injectIntl(FieldForm));

const createFormValueSelector = formValueSelector(CREATE_FIELD_FORM);

const createFormState = state => ({
  activePanelId: getActivePanelId(state),
  fieldDefinitions: getFields(state),
  fieldNames: getFieldNames(state),
  isCreate: true,
  metadataDefinitions: getMetadataDefinitions(state),
  metadataMappings: getMetadataMappings(state),
  systemName: createFormValueSelector(state, 'systemName'),
  type: createFormValueSelector(state, 'type'),
});

const makeForm = (form, stateFunction) =>
  connect(
    stateFunction,
    (dispatch: Dispatch) =>
      bindActionCreatorsForm({
        createMetadataMappings,
        deleteMetadataMappings,
        getFieldsAction,
        hideDialogPanel,
        removeAllPredefinedValuesAction,
        setEmbedMetadataMapping,
        setSelectedFieldsListItemName,
        showDialogPanel,
        showModalPrompt,
        updateFieldsAction,
        updatePredefinedList,
      })
  )(injectIntl(form));

export default makeForm(editForm, editFormState);

export const CreateFieldForm = makeForm(reduxCreateForm, createFormState);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/FieldForm.js